// Pure helpers for the "Receiving Time" widget, kept free of React so they can be
// unit tested on their own (same split as writtenSalesHelpers / estimatingTurnaroundHelpers).
//
// The API (GET /Dashboard/api/Dashboard/ReceivingTime and ReceivingTimeDetail) does
// all the counting and averaging; these helpers only reshape and format.
//
// Two kinds of timestamp come back and must not be confused:
//   * "...Local" fields (startLocal, putAwayLocal, endLocal, createdAtLocal, asOfLocal)
//     are Denver wall-clock times with no zone — what the camera watcher typed. They
//     are formatted from their digits, never through Date, so a viewer in another
//     time zone still sees the time the truck was actually unloaded.
//   * "...Utc" fields (clip times, comments, status changes, lastSyncedUtc) are
//     instants, shown in Denver time.

const DENVER = "America/Denver";

const EMPTY_WEEK = {
  label: "",
  trucksReceived: 0,
  countedInAverage: 0,
  averageMinutes: null,
  averageDisplay: "—",
  notPutAway: 0,
  flagged: 0,
  qualityChecked: 0,
  receivedCorrectly: 0,
  receivedCorrectlyPercent: null,
  checks: [],
  standardMet: null,
  standardMisses: [],
};

const firstResult = (data) => {
  if (Array.isArray(data)) return data[0] || null;
  if (data && Array.isArray(data.result)) return data.result[0] || null;
  return null;
};

const normalizeWeek = (w) => {
  const week = { ...EMPTY_WEEK, ...(w || {}) };
  return {
    ...week,
    checks: Array.isArray(week.checks) ? week.checks : [],
    standardMisses: Array.isArray(week.standardMisses) ? week.standardMisses : [],
  };
};

// Map the ReceivingTime response (axios `res.data`) to the widget's view model.
export const normalizeReceivingTime = (data) => {
  const r = firstResult(data);
  if (!r) {
    return { connected: false, hasData: false, lastSyncedUtc: null, syncProblem: null, asOfLocal: null, currentWeek: normalizeWeek(), trend: [] };
  }
  return {
    connected: !!r.connected,
    hasData: !!r.hasData,
    lastSyncedUtc: r.lastSyncedUtc || null,
    // Set when the latest refresh from Spot AI failed; the figures are then as of the last good one.
    syncProblem: r.syncProblem || null,
    asOfLocal: r.asOfLocal || null,
    currentWeek: normalizeWeek(r.currentWeek),
    trend: Array.isArray(r.trend) ? r.trend.map(normalizeWeek) : [],
  };
};

export const normalizeCases = (data) => {
  const rows = Array.isArray(data) ? data : data && Array.isArray(data.result) ? data.result : [];
  return rows.map((c) => ({
    ...c,
    flags: Array.isArray(c.flags) ? c.flags : [],
    checks: Array.isArray(c.checks) ? c.checks : [],
    detail: {
      fields: [],
      comments: [],
      annotations: [],
      clips: [],
      events: [],
      tags: [],
      ...(c.detail || {}),
    },
  }));
};

// ---------------------------------------------------------------- formatting

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// "2026-10-05T09:44:00" -> { y, mo, d, h, mi } from the digits, no time-zone math.
const wallClock = (v) => {
  if (!v) return null;
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/);
  if (!m) return null;
  return { y: +m[1], mo: +m[2], d: +m[3], h: m[4] != null ? +m[4] : null, mi: m[5] != null ? +m[5] : null };
};

const weekday = (w) => DAYS[new Date(w.y, w.mo - 1, w.d).getDay()];

const clock = (w) => {
  if (w.h == null) return "";
  const am = w.h < 12;
  const h12 = w.h % 12 === 0 ? 12 : w.h % 12;
  return `${h12}:${String(w.mi).padStart(2, "0")} ${am ? "AM" : "PM"}`;
};

/** "Mon 10/5" */
export const fmtDay = (v) => {
  const w = wallClock(v);
  return w ? `${weekday(w)} ${w.mo}/${w.d}` : "";
};

/** "9:44 AM" */
export const fmtTime = (v) => {
  const w = wallClock(v);
  return w ? clock(w) : "";
};

/** "Mon 10/5 9:44 AM" */
export const fmtDayTime = (v) => {
  const w = wallClock(v);
  return w ? `${weekday(w)} ${w.mo}/${w.d} ${clock(w)}`.trim() : "";
};

/** "Oct 5, 2026" */
export const fmtLongDate = (v) => {
  const w = wallClock(v);
  return w ? `${MONTHS[w.mo - 1]} ${w.d}, ${w.y}` : "";
};

// The API serializes some instants without a trailing Z; they are UTC regardless.
const asInstant = (v) => {
  if (!v) return null;
  const s = String(v);
  const d = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(s) ? s : `${s}Z`);
  return isNaN(d.getTime()) ? null : d;
};

/** A UTC instant shown in Denver time: "Mon 10/5 9:32 AM". */
export const fmtInstant = (v) => {
  const d = asInstant(v);
  if (!d) return "";
  return d
    .toLocaleString("en-US", {
      timeZone: DENVER,
      weekday: "short",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    })
    .replace(/,/g, "");
};

/**
 * The trend without the empty weeks before the first truck was ever logged, so a new
 * install doesn't open on a row of blank bars. Always keeps the current (last) week.
 */
export const visibleTrend = (trend) => {
  const first = trend.findIndex((w) => w.trucksReceived > 0);
  if (first < 0) return trend.slice(-1);
  return trend.slice(Math.min(first, trend.length - 1));
};

/** Minutes -> "42m", "3h 42m", "1d 19h". Mirrors the API's formatting. */
export const fmtDuration = (minutes) => {
  if (minutes == null || isNaN(minutes) || minutes < 0) return "—";
  const total = Math.round(minutes);
  const days = Math.floor(total / 1440);
  const hours = Math.floor((total % 1440) / 60);
  const mins = total % 60;
  if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  if (hours > 0) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  return `${mins}m`;
};

/** yyyy-MM-dd for the detail request. */
export const isoDay = (v) => {
  const c = wallClock(v);
  return c ? `${c.y}-${String(c.mo).padStart(2, "0")}-${String(c.d).padStart(2, "0")}` : "";
};

// ------------------------------------------------------- receiving checks
//
// Three Yes/No questions on each Spot AI case: labelling standard met, everything
// together in receiving hold, and the physical bin matches Business Central. The API
// decides "received correctly" (all three Yes; any No is not correct; blanks with no
// No are not checked yet) — these only display it.

/** A check's answer on one truck: true, false or null (not answered). */
export const checkAnswer = (c, key) => {
  const k = (c.checks || []).find((x) => x.key === key);
  return k ? k.passed : null;
};

/** "Yes" / "No" / "" for the export. */
export const yesNo = (passed) => (passed === true ? "Yes" : passed === false ? "No" : "");

// ------------------------------------------------------ receiving standard
//
// The API decides the week's verdict: average put-away under 24 hours and not one No
// on any of the three checks (a blank doesn't fail it). standardMet is null when
// nothing failed but there's no average to judge yet.

/** "Standard met" / "Standard not met" / "" when it can't be judged. */
export const standardText = (w) =>
  w.standardMet === true ? "Standard met" : w.standardMet === false ? "Standard not met" : "";

/** The circle's colour: rt-calm (met), rt-alert (not met) or rt-unknown. */
export const standardClass = (w) =>
  w && w.standardMet === true ? "rt-calm" : w && w.standardMet === false ? "rt-alert" : "rt-unknown";

// ------------------------------------------------------------ Excel export

export const EXPORT_COLUMNS = [
  { key: "caseId", label: "Spot AI Case", type: "number" },
  { key: "title", label: "Truck" },
  { key: "receivedDay", label: "Received" },
  { key: "status", label: "Status" },
  { key: "supplier", label: "Supplier" },
  { key: "materialType", label: "Material" },
  { key: "pallets", label: "# of Pallets" },
  { key: "rolls", label: "# of Rolls" },
  { key: "bins", label: "Bins" },
  { key: "itemDescription", label: "Item Description" },
  { key: "unloadedAt", label: "Clock Started" },
  { key: "putAwayAt", label: "Put Away" },
  { key: "durationDisplay", label: "Time to Put Away" },
  { key: "durationMinutes", label: "Minutes", type: "number" },
  { key: "inAverage", label: "In Average" },
  { key: "labelsCheck", label: "Label Standard Met" },
  { key: "holdCheck", label: "All Together in Receiving Hold" },
  { key: "binCheck", label: "Bin Matches Business Central" },
  { key: "correctText", label: "Received Correctly" },
  { key: "flagText", label: "Needs a Look" },
  { key: "createdBy", label: "Logged By" },
  { key: "clipCount", label: "Clips", type: "number" },
  { key: "description", label: "Description" },
];

export const toExportRows = (cases) =>
  cases.map((c) => ({
    ...c,
    receivedDay: fmtLongDate(c.incidentDate),
    unloadedAt: fmtDayTime(c.startLocal) + (c.startFromCaseCreated ? " (case opened)" : ""),
    putAwayAt: c.isPutAway ? fmtDayTime(c.putAwayLocal) : "Not yet",
    durationDisplay: c.countsInAverage ? `${c.durationDisplay}${c.isPutAway ? "" : " so far"}` : "Check times",
    inAverage: c.countsInAverage ? "Yes" : "No",
    labelsCheck: yesNo(checkAnswer(c, "labels")),
    holdCheck: yesNo(checkAnswer(c, "hold")),
    binCheck: yesNo(checkAnswer(c, "bin")),
    correctText: c.receivedCorrectly === true ? "Yes" : c.receivedCorrectly === false ? "No" : "Not checked",
    flagText: c.flags.join(" "),
  }));
